import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import FollowUpStudentsTable from '@/tables/FollowUpStudentsTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-up-students/')({
  component: FollowUpStudentsScreen,
});

/**
 * The *All Students* tab both follow-up group shells mount —
 * `plugins/wbs-core/modules/Datatable/Tables/FollowUps/Students.php`, rendered
 * inside the Partners tab of a coordinator's group and the Coordinators tab of
 * a partner's group.
 *
 * ONE ROUTE FOR BOTH ROLES, where legacy has one tab per group. The roster is
 * the caller's own caseload and the endpoint branches on their subtype, so
 * there is nothing left for the URL to distinguish once D24 has removed the
 * group — the same re-home the two directories take.
 *
 * BOTH ROLES, AND NO ADMIN. `FOLLOW_UP_STUDENTS_REQUIREMENT` is
 * `profile_types=FOLLOW_UPS, admin_bypass=False` (`follow_ups/permissions.py`),
 * and the bypass is off for a measured reason rather than by default: an
 * administrator has a row in neither `followup_coordinators` nor
 * `followup_partners`, so a bypass would serve them a guaranteed-empty tab —
 * a silent lie where a refusal is honest. There is deliberately no admin nav
 * item either.
 */
function FollowUpStudentsScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, never `useCurrentUser().isStudyHelper` — that folds
  // STUDY_HELPER and ADMIN in with the two follow-up roles
  // (`hooks/useCurrentUser.ts:14-19`) and would render this page for callers the
  // endpoint answers 403 to. Defence in depth over the same set the endpoint
  // enforces, never the only gate; `isPending` first, or the coordinator
  // bounces themselves while `/users/me` is still in flight.
  const canView =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
    me?.profile_type === 'FOLLOWUP_PARTNER';

  if (isPending) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        {/* A bare `Title`, not `TitleWithQuestion`: legacy hangs
            `wbs_contextual_helper` off the enclosing tab's own `h3`, that store
            is not ported, and `HelpModal` falls back silently to its FIRST item
            on an id miss (`common/HelpModal.tsx:42-47`). */}
        <Title>{t('lessons.all-students')}</Title>
      </GroupHeaderWrapper>
      <FollowUpStudentsTable />
    </PageContainer>
  );
}
